package top.wcpe.allincore.ui.web

internal data class WebRenderResourceCounts(
    val displayObjects: Int,
    val ownedTextures: Int,
    val borrowedTextures: Int,
    val ownedBaseTextures: Int,
    val borrowedBaseTextures: Int,
    val masks: Int,
    val filters: Int,
    val renderTextures: Int,
    val objectUrls: Int,
    val animationFrames: Int,
    val asyncTasks: Int,
) {
    val textures: Int get() = ownedTextures + borrowedTextures
    val renderTargets: Int get() = renderTextures

    companion object {
        val ZERO = WebRenderResourceCounts(0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0)
    }
}

internal enum class WebRenderResourceKind {
    DISPLAY_OBJECT,
    OWNED_TEXTURE,
    BORROWED_TEXTURE,
    OWNED_BASE_TEXTURE,
    BORROWED_BASE_TEXTURE,
    MASK,
    FILTER,
    RENDER_TEXTURE,
    OBJECT_URL,
    ANIMATION_FRAME,
    ASYNC_TASK,
}

internal class WebRenderResourceLease(
    private val registry: WebRenderResourceRegistry,
    private val kind: WebRenderResourceKind,
    private val releaseResource: () -> Unit,
) {
    private var released = false

    fun release(): Boolean {
        if (released) return true
        val success = runCatching(releaseResource).isSuccess
        if (success) {
            released = true
            registry.release(kind, this)
        }
        return success
    }
}

internal class WebRenderResourceRegistry {
    private val leases = mutableMapOf<WebRenderResourceKind, MutableSet<WebRenderResourceLease>>()

    fun register(kind: WebRenderResourceKind, release: () -> Unit = {}): WebRenderResourceLease {
        val lease = WebRenderResourceLease(this, kind, release)
        leases.getOrPut(kind) { mutableSetOf() }.add(lease)
        return lease
    }

    fun snapshot(): WebRenderResourceCounts = WebRenderResourceCounts(
        displayObjects = count(WebRenderResourceKind.DISPLAY_OBJECT),
        ownedTextures = count(WebRenderResourceKind.OWNED_TEXTURE),
        borrowedTextures = count(WebRenderResourceKind.BORROWED_TEXTURE),
        ownedBaseTextures = count(WebRenderResourceKind.OWNED_BASE_TEXTURE),
        borrowedBaseTextures = count(WebRenderResourceKind.BORROWED_BASE_TEXTURE),
        masks = count(WebRenderResourceKind.MASK),
        filters = count(WebRenderResourceKind.FILTER),
        renderTextures = count(WebRenderResourceKind.RENDER_TEXTURE),
        objectUrls = count(WebRenderResourceKind.OBJECT_URL),
        animationFrames = count(WebRenderResourceKind.ANIMATION_FRAME),
        asyncTasks = count(WebRenderResourceKind.ASYNC_TASK),
    )

    fun disposeAll(): Boolean = leases.values
        .flatMap { it.toList() }
        .fold(true) { success, lease -> lease.release() && success }

    internal fun release(kind: WebRenderResourceKind, lease: WebRenderResourceLease) {
        leases[kind]?.remove(lease)
    }

    private fun count(kind: WebRenderResourceKind): Int = leases[kind]?.size ?: 0
}

internal class WebTextureLease(
    val resourceKey: String,
    val texture: Texture,
    private val leases: List<WebRenderResourceLease>,
) {
    private var released = false

    fun release(): Boolean {
        if (released) return true
        val success = leases.fold(true) { result, lease -> lease.release() && result }
        if (success) released = true
        return success
    }
}
